<template>
  <div>
    <v-alert
      v-if="errorMessage"
      type="error"
      text
      dismissible
      class="mx-5 mt-4 mb-0"
    >
      {{ errorMessage }}
    </v-alert>

    <v-card flat class="rmr-table-shell social-revenue-table">
      <rmr-table-header>
        <template #heading>
          <div class="rmr-table-shell__eyebrow">Monétisation</div>
          <div class="rmr-table-shell__title">
            Revenus {{ networkLabel }} · {{ filters.year }}
          </div>
        </template>

        <template #controls>
          <div v-if="hasTotals" class="social-revenue-table__summary">
            <div class="social-revenue-table__summary-block">
              <div class="social-revenue-table__summary-label">
                Total {{ filters.year }}
              </div>
              <div class="social-revenue-table__summary-value">
                {{ formatAmount(totals.total_amount, totalsCurrency) }}
              </div>
            </div>
            <div
              v-if="totals.landing_amount !== null"
              class="social-revenue-table__summary-block"
            >
              <div class="social-revenue-table__summary-label">
                Atterrissage
              </div>
              <div
                class="social-revenue-table__summary-value social-revenue-table__landing"
              >
                {{ formatAmount(totals.landing_amount, totalsCurrency) }}
              </div>
            </div>
          </div>
        </template>
      </rmr-table-header>

      <social-reporting-filters
        v-model="filters"
        :options="networkFilterOptions"
        :loading="loading"
        :show-dates="false"
        :show-network="false"
        :show-brand="false"
        :show-account="false"
        :show-source="false"
        :show-year="true"
        :years="years"
        site-label="Sites"
        @apply="applyFilters"
        @reset="resetFilters"
      >
        <template #actions>
          <rmr-action-button
            label="Exporter Excel"
            :icon="icons.mdiMicrosoftExcel"
            variant="primary"
            size="sm"
            :color="RMR_GREEN"
            :loading="exporting"
            :disabled="loading || !rows.length || network !== 'facebook'"
            @click="exportExcel"
          />
        </template>
      </social-reporting-filters>

      <v-card-text class="rmr-table-shell__body">
        <v-data-table
          :headers="headers"
          :items="normalizedRows"
          :loading="loading"
          :options.sync="tableOptions"
          multi-sort
          :footer-props="{ 'items-per-page-options': [25, 50, 100, 200] }"
          locale="fr-FR"
          class="rmr-data-table"
        >
          <template v-slot:loading>
            <div class="rmr-data-table__state">
              <v-progress-circular
                indeterminate
                :color="RMR_INDIGO"
                size="28"
                class="mb-2"
              />
              <div class="rmr-data-table__state-title">
                Chargement des revenus sociaux
              </div>
            </div>
          </template>

          <template v-slot:body.prepend>
            <tr v-if="hasTotals" class="social-revenue-table__totals">
              <td class="social-revenue-table__totals-label">TOTAL</td>
              <td></td>
              <td
                v-for="month in months"
                :key="month.key"
                class="text-end text-no-wrap"
              >
                {{ formatAmount(totals.months[month.key], totalsCurrency) }}
              </td>
              <td class="text-end text-no-wrap">
                {{ formatAmount(totals.total_amount, totalsCurrency) }}
              </td>
              <td
                class="text-end text-no-wrap social-revenue-table__landing"
              >
                {{ formatAmount(totals.landing_amount, totalsCurrency) }}
              </td>
            </tr>
          </template>

          <template
            v-for="month in months"
            v-slot:[`item.month_${month.key}`]="{ item }"
          >
            <span :key="month.key" class="text-no-wrap">
              {{ formatAmount(item[`month_${month.key}`], item.currency) }}
            </span>
          </template>

          <template v-slot:item.total_amount="{ item }">
            <strong class="text-no-wrap">
              {{ formatAmount(item.total_amount, item.currency) }}
            </strong>
          </template>

          <template v-slot:item.landing_amount="{ item }">
            <strong class="text-no-wrap social-revenue-table__landing">
              {{ formatAmount(item.landing_amount, item.currency) }}
            </strong>
          </template>

          <template v-slot:no-data>
            <div class="rmr-data-table__state">
              <v-icon :color="RMR_TEXT_SUBTLE" size="32" class="mb-2">
                {{ icons.mdiCashRemove }}
              </v-icon>
              <div class="rmr-data-table__state-title">
                Aucune donnée disponible
              </div>
              <div class="rmr-data-table__state-text">
                <template v-if="network !== 'facebook'">
                  Les revenus {{ networkLabel }} ne sont pas encore connectés à
                  RMR.
                </template>
                <template v-else>
                  Aucun revenu Facebook ne correspond aux filtres sélectionnés.
                </template>
              </div>
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import isEqual from "lodash/isEqual";
import { mdiCashRemove, mdiMicrosoftExcel } from "@mdi/js";

import useAxios from "@/hooks/useAxios";
import RmrActionButton from "@/components/ui/actions/RmrActionButton.vue";
import RmrTableHeader from "@/components/ui/layout/RmrTableHeader.vue";
import { RMR_GREEN, RMR_INDIGO, RMR_TEXT_SUBTLE } from "@/styles/tokens";
import SocialReportingFilters, {
  emptyFilters,
} from "./SocialReportingFilters.vue";
import { NETWORK_LABELS } from "./config";
import {
  readQueryList,
  readQueryString,
  replaceQueryParams,
} from "./queryState";

const FILTERS_ENDPOINT = "/audience/social-reporting/filters";
const MATRIX_ENDPOINT = "/audience/social-reporting/revenues/matrix";
const REVENUE_FILTER_QUERY_KEYS = ["year", "site_ids"];
const MONTHS = [
  ["01", "Janvier"],
  ["02", "Février"],
  ["03", "Mars"],
  ["04", "Avril"],
  ["05", "Mai"],
  ["06", "Juin"],
  ["07", "Juillet"],
  ["08", "Août"],
  ["09", "Septembre"],
  ["10", "Octobre"],
  ["11", "Novembre"],
  ["12", "Décembre"],
].map(([key, label]) => ({ key, label }));

const valueOrUndefined = (values) =>
  values && values.length ? values.join(",") : undefined;

const revenueFilters = (query = {}) => {
  const currentYear = new Date().getFullYear();
  const requestedYear = Number.parseInt(readQueryString(query.year), 10);
  return {
    ...emptyFilters(),
    year:
      Number.isInteger(requestedYear) &&
      requestedYear >= 2026 &&
      requestedYear <= currentYear
        ? requestedYear
        : currentYear,
    siteIds: readQueryList(query.site_ids, { integer: true }),
  };
};

const safeExcelText = (value) => {
  if (value === null || value === undefined) return "";
  const text = String(value);
  return /^[=+\-@]/.test(text.trimStart()) ? `'${text}` : text;
};

const downloadBlob = (blob, filename) => {
  const link = document.createElement("a");
  const objectUrl = URL.createObjectURL(blob);
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
};

export default {
  name: "SocialRevenueMatrix",
  components: {
    RmrActionButton,
    RmrTableHeader,
    SocialReportingFilters,
  },
  props: {
    network: {
      type: String,
      required: true,
    },
  },
  setup() {
    const { axiosGet } = useAxios();
    return {
      axiosGet,
      icons: { mdiCashRemove, mdiMicrosoftExcel },
      RMR_GREEN,
      RMR_INDIGO,
      RMR_TEXT_SUBTLE,
    };
  },
  data() {
    return {
      filters: revenueFilters(this.$route.query),
      allFilterOptions: {
        sources: [],
        brands: [],
        networks: [],
        accounts: [],
        sites: [],
        authors: [],
        postTypes: [],
      },
      rows: [],
      loading: false,
      exporting: false,
      errorMessage: "",
      requestSequence: 0,
      months: MONTHS,
      tableOptions: {
        page: 1,
        itemsPerPage: 50,
        sortBy: ["site_priority", "source_brand_name", "account_name"],
        sortDesc: [true, false, false],
      },
    };
  },
  computed: {
    networkLabel() {
      return NETWORK_LABELS[this.network] || this.network;
    },
    years() {
      const currentYear = new Date().getFullYear();
      return Array.from(
        { length: Math.max(currentYear - 2026 + 1, 1) },
        (_value, index) => currentYear - index
      );
    },
    networkFilterOptions() {
      return {
        ...this.allFilterOptions,
        accounts: this.allFilterOptions.accounts.filter(
          (account) => account.network === this.network
        ),
      };
    },
    headers() {
      return [
        { text: "Marque", value: "source_brand_name" },
        { text: "Compte", value: "account_name" },
        ...this.months.map((month) => ({
          text: month.label,
          value: `month_${month.key}`,
          align: "end",
        })),
        { text: "Total", value: "total_amount", align: "end" },
        { text: "ATTERRISSAGE", value: "landing_amount", align: "end" },
      ];
    },
    normalizedRows() {
      return this.rows.map((row) => {
        const normalized = {
          ...row,
          // Accounts mapped to an RMR site are displayed before unmapped ones.
          site_priority:
            row.site_id === null || row.site_id === undefined ? 0 : 1,
        };
        this.months.forEach((month) => {
          const amount = row.months?.[month.key];
          normalized[`month_${month.key}`] =
            amount === null || amount === undefined ? null : Number(amount);
        });
        normalized.total_amount =
          row.total_amount === null || row.total_amount === undefined
            ? null
            : Number(row.total_amount);
        normalized.landing_amount =
          row.landing_amount === null || row.landing_amount === undefined
            ? null
            : Number(row.landing_amount);
        return normalized;
      });
    },
    hasTotals() {
      return this.normalizedRows.length > 0;
    },
    totalsCurrency() {
      // The Nonli crawler stores every amount in EUR. The guard keeps a future
      // multi-currency source from silently producing a mixed-currency total.
      const currencies = new Set(
        this.rows.map((row) => row.currency).filter(Boolean)
      );
      return currencies.size === 1 ? [...currencies][0] : "EUR";
    },
    totals() {
      // Totals cover every filtered row, not just the page being displayed.
      // A column stays null when no row carries a value, so an absent month
      // still reads as "—" instead of a misleading 0 €.
      const add = (current, value) =>
        value === null || value === undefined
          ? current
          : (current || 0) + Number(value);

      const months = {};
      this.months.forEach((month) => {
        months[month.key] = null;
      });
      let totalAmount = null;
      let landingAmount = null;

      this.normalizedRows.forEach((row) => {
        this.months.forEach((month) => {
          months[month.key] = add(months[month.key], row[`month_${month.key}`]);
        });
        totalAmount = add(totalAmount, row.total_amount);
        landingAmount = add(landingAmount, row.landing_amount);
      });

      return {
        months,
        total_amount: totalAmount,
        landing_amount: landingAmount,
      };
    },
  },
  watch: {
    network() {
      this.filters = { ...this.filters, accountIds: [] };
      this.tableOptions.page = 1;
      this.fetchRows();
    },
    "$route.query": {
      deep: true,
      handler(query) {
        const restoredFilters = revenueFilters(query);
        if (isEqual(restoredFilters, this.filters)) return;
        this.filters = restoredFilters;
        this.tableOptions.page = 1;
        this.fetchRows();
      },
    },
  },
  created() {
    this.fetchFilterOptions();
    this.fetchRows();
  },
  methods: {
    async fetchFilterOptions() {
      try {
        const response = await this.axiosGet(FILTERS_ENDPOINT, {
          resource: "revenues",
        });
        const data = response.data || {};
        this.allFilterOptions = {
          sources: data.sources || [],
          brands: (data.brands || []).map((brand) => ({
            ...brand,
            displayName: brand.name || brand.id,
          })),
          networks: data.networks || [],
          accounts: data.accounts || [],
          sites: (data.sites || []).map((site) => ({
            ...site,
            displayName: site.domain
              ? `${site.name || site.domain} · ${site.domain}`
              : site.name,
          })),
          authors: data.authors || [],
          postTypes: data.post_types || [],
        };
      } catch (error) {
        this.errorMessage = "Impossible de charger les valeurs de filtres.";
      }
    },
    buildParams() {
      return {
        year: this.filters.year,
        networks: this.network,
        brand_ids: valueOrUndefined(this.filters.brandIds),
        account_ids: valueOrUndefined(this.filters.accountIds),
        site_ids: valueOrUndefined(this.filters.siteIds),
        sources: valueOrUndefined(this.filters.sources),
      };
    },
    async fetchRows() {
      const requestId = ++this.requestSequence;
      this.errorMessage = "";
      if (this.network !== "facebook") {
        this.rows = [];
        this.loading = false;
        return;
      }
      this.loading = true;
      try {
        const response = await this.axiosGet(
          MATRIX_ENDPOINT,
          this.buildParams()
        );
        if (requestId !== this.requestSequence || this.network !== "facebook") {
          return;
        }
        this.rows = response.data.rows || [];
      } catch (error) {
        if (requestId !== this.requestSequence) return;
        this.rows = [];
        this.errorMessage = "Impossible de charger les revenus sociaux.";
      } finally {
        if (requestId === this.requestSequence) {
          this.loading = false;
        }
      }
    },
    syncFiltersToUrl() {
      return replaceQueryParams(
        this.$router,
        this.$route,
        REVENUE_FILTER_QUERY_KEYS,
        {
          year: this.filters.year,
          site_ids: this.filters.siteIds,
        }
      );
    },
    async applyFilters() {
      await this.syncFiltersToUrl();
      this.tableOptions.page = 1;
      this.fetchRows();
    },
    async resetFilters() {
      this.filters = revenueFilters();
      await this.syncFiltersToUrl();
      this.tableOptions.page = 1;
      this.fetchRows();
    },
    formatAmount(value, currency = "EUR") {
      if (value === null || value === undefined) return "—";
      return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: currency || "EUR",
        maximumFractionDigits: 2,
      }).format(value);
    },
    async exportExcel() {
      this.exporting = true;
      try {
        const XLSX = await import("xlsx");
        const sortedRows = [...this.normalizedRows].sort(
          (left, right) =>
            right.site_priority - left.site_priority ||
            (left.source_brand_name || "").localeCompare(
              right.source_brand_name || "",
              "fr",
              { sensitivity: "base" }
            ) ||
            (left.account_name || "").localeCompare(
              right.account_name || "",
              "fr",
              { sensitivity: "base" }
            )
        );
        const excelRows = sortedRows.map((row) => {
          const result = {
            Marque: safeExcelText(row.source_brand_name),
            Compte: safeExcelText(row.account_name),
          };
          this.months.forEach((month) => {
            result[month.label] = row[`month_${month.key}`];
          });
          result.Total = row.total_amount;
          result.ATTERRISSAGE = row.landing_amount;
          return result;
        });
        const worksheet = XLSX.utils.json_to_sheet(excelRows);
        worksheet["!autofilter"] = { ref: worksheet["!ref"] || "A1" };
        worksheet["!cols"] = [
          { wch: 24 },
          { wch: 28 },
          ...this.months.map(() => ({ wch: 15 })),
          { wch: 16 },
          { wch: 18 },
        ];
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Revenus");
        const excelBuffer = XLSX.write(workbook, {
          bookType: "xlsx",
          type: "array",
        });
        downloadBlob(
          new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          }),
          `revenus-${this.network}-${this.filters.year}.xlsx`
        );
      } catch (error) {
        this.errorMessage = "L’export Excel n’a pas pu être généré.";
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.social-revenue-table__landing {
  color: $rmr-indigo-dark;
}

.social-revenue-table__totals {
  background: $rmr-surface-2;

  td {
    font-weight: 700;
    color: $rmr-text-primary;
    border-bottom: 2px solid $rmr-border-hover !important;
  }
}

.social-revenue-table__totals-label {
  letter-spacing: 0.04em;
  color: $rmr-text-secondary;
}

.social-revenue-table__summary {
  display: flex;
  gap: $rmr-space-lg;
  align-items: flex-end;
}

.social-revenue-table__summary-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: $rmr-text-subtle;
}

.social-revenue-table__summary-value {
  font-size: 1.25rem;
  font-weight: 700;
  line-height: 1.2;
  color: $rmr-text-primary;
  white-space: nowrap;
}
</style>
